import { CombinedGraphQLErrors } from '@apollo/client';
import { ErrorLink } from '@apollo/client/link/error';
import * as Sentry from '@sentry/react';
import { GraphQLFormattedError } from 'graphql';

import { GraphQLErrorCode } from '../../helpers/graphqlErrors';
import { notifySessionCompanyChanged } from '../../lib/companySession';
import { markLeavingPage } from '../../lib/navigation/pageExit';

/** List of error messages that should not be reported to Sentry */
const ignoredErrorMessages = ['Not Authorised!', 'Not Authenticated'];

/** Check if an error should be ignored based on its message */
const shouldIgnoreErrorFromMessage = (errorMessage: string): boolean => {
  return ignoredErrorMessages.some((ignoredMsg) => errorMessage.includes(ignoredMsg));
};

/** List of error codes that should not be reported to Sentry */
const ignoredErrorCodes = ['BAD_USER_INPUT', 'BAD_REQUEST', 'FORBIDDEN', 'PAYMENT_FAILED'];

/** Check if an error should be ignored based on its extensions */
const shouldIgnoreErrorFromExtensions = (extensions: GraphQLFormattedError['extensions'] | undefined): boolean => {
  if (!extensions) {
    return false;
  }

  return ignoredErrorCodes.some((ignoredCode) => extensions.code === ignoredCode);
};

/**
 * If a request comes back with our authentication-rule error, the user's WorkOS session
 * cookie has either expired or been invalidated. Bounce them to /login and forward the
 * page they were on as `return_to` so the post-AuthKit callback drops them back where they
 * were. Distinct from `Not Authorised!` (the default graphql-shield message for any rule
 * failure) so a signed-in user hitting a forbidden mutation does not get redirected.
 *
 * Guard against redirect loops: if we are already on the auth-related routes, skip — the
 * session-recovery flow is already in progress and another navigation would clobber it.
 * `/logout` is in that list because in-flight queries reliably fail there (the cookie has
 * just been cleared) and redirecting would hand `/login` a `return_to` pointing back at
 * sign-out — re-arming the very login→logout loop the sign-out was ending.
 */
const AUTH_ROUTES = ['/login', '/auth/callback', '/logout'];
function redirectToLoginIfNeeded(): void {
  const path = window.location.pathname;
  // Match on whole path segments: a page like `/logout-confirmation` merely shares a prefix
  // with `/logout` and must still be able to recover its session.
  if (AUTH_ROUTES.some((route) => path === route || path.startsWith(`${route}/`))) {
    return;
  }
  const returnTo = window.location.pathname + window.location.search;
  markLeavingPage();
  window.location.assign(`/login?return_to=${encodeURIComponent(returnTo)}`);
}

/**
 * Describe a GraphQL error for Sentry.
 *
 * Why this exists: Apollo hands the link a plain `GraphQLFormattedError`, which Sentry can neither
 * title nor group, so every unrelated failure collapsed into one `<anonymous>` issue. The server also
 * redacts anything unexpected to a single generic message, leaving the field that failed as the only
 * discriminator. An `Error` raised here would still share an identical stack with every other one and
 * regroup, so the caller pairs this name with the fingerprint below.
 */
export function describeGraphQLError(
  error: GraphQLFormattedError,
  operationName: string | undefined,
): { field: string; code: string; message: string; fingerprint: string[] } {
  const path = error.path?.join('.');
  const field = path && path.length > 0 ? path : (operationName ?? 'unknown');
  const code = typeof error.extensions?.code === 'string' ? error.extensions.code : 'UNKNOWN';

  return {
    field,
    code,
    message: `GraphQL ${field}: ${error.message}`,
    fingerprint: ['graphql', field, code],
  };
}

/** Apollo error link */
export const errorLink = new ErrorLink(({ error, operation }) => {
  if (CombinedGraphQLErrors.is(error)) {
    if (error.errors.some((err) => err.message === 'Not Authenticated')) {
      redirectToLoginIfNeeded();
      return;
    }

    // The session moved to another company (another tab, an expired act-as, a rule that no longer allows it). Not a fault
    // to report: the viewer provider reloads the viewer and takes the user home.
    if (error.errors.some((err) => err.extensions?.code === GraphQLErrorCode.SessionCompanyChanged)) {
      notifySessionCompanyChanged();
      return;
    }

    error.errors.forEach((err) => {
      // Check if this is an error we want to ignore
      if (shouldIgnoreErrorFromMessage(err.message) || shouldIgnoreErrorFromExtensions(err.extensions)) {
        // Skip reporting this error to Sentry
        return;
      }

      // Report all other errors to Sentry, named and fingerprinted so each one gets its own issue.
      const { field, code, message, fingerprint } = describeGraphQLError(err, operation.operationName);
      const reported = new Error(message);
      reported.name = 'GraphQLOperationError';

      Sentry.captureException(reported, {
        fingerprint,
        tags: { graphqlField: field, graphqlCode: code },
        extra: { operationName: operation.operationName, path: err.path },
      });
    });
  }
});
